import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { toast } from 'sonner'
import { Bell } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'

/**
 * NotificationBell.
 *
 * Self-contained bell + dropdown panel. Rendered in up to three layout
 * contexts via the `variant` prop, all sharing the same react-query cache
 * via stable queryKeys so the desktop sidebar instance and the mobile
 * top-bar instance dedupe.
 *
 *   variant='sidebar'  + collapsed=false → icon + "Notifications" + count pill
 *   variant='sidebar'  + collapsed=true  → icon-only + overlaid count dot
 *   variant='topbar'   → icon + overlaid count dot (mobile sticky top bar)
 *
 * Panel: rendered via portal into document.body (escapes the sidebar's
 * overflow:hidden), positioned via fixed coords computed from the trigger's
 * bounding rect at open time. Anchors right of trigger on sidebar variant,
 * below-right on mobile topbar variant.
 *
 * Data: 30s poll (matching AppLayout's TeamChat-unread pattern at lines
 * 122-159) — realtime intentionally avoided for the same decoupling reason.
 */

type Variant = 'sidebar' | 'topbar'

type Notification = {
  id: string
  type: string
  title: string
  body: string | null
  link: string | null
  read_at: string | null
  created_at: string
  target_user_id: string | null
}

const PANEL_WIDTH = 380
const PANEL_LIST_LIMIT = 20
const POLL_MS = 30000

export default function NotificationBell({
  variant, collapsed = false,
}: { variant: Variant; collapsed?: boolean }) {
  const { profile, workspace } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [panelOpen, setPanelOpen] = useState(false)
  const [panelCoords, setPanelCoords] = useState<{ top: number; left: number; width: number } | null>(null)

  const wsId = workspace?.id
  const pid = profile?.id

  // ─── Queries (shared queryKeys → desktop+mobile instances dedupe) ──────────
  const listKey = ['notifications', wsId, pid] as const
  const countKey = ['notifications-unread-count', wsId, pid] as const

  const { data: items = [] } = useQuery({
    queryKey: listKey,
    enabled: !!wsId && !!pid,
    refetchInterval: POLL_MS,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, type, title, body, link, read_at, created_at, target_user_id')
        .order('created_at', { ascending: false })
        .limit(PANEL_LIST_LIMIT)
      if (error) throw error
      return (data ?? []) as Notification[]
    },
  })

  const { data: unreadCount = 0 } = useQuery({
    queryKey: countKey,
    enabled: !!wsId && !!pid,
    refetchInterval: POLL_MS,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .is('read_at', null)
      if (error) throw error
      return count ?? 0
    },
  })

  // ─── Panel open / close + positioning ──────────────────────────────────────
  const openPanel = () => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    if (variant === 'sidebar') {
      // Anchor to the right of the trigger. Clamp top so the panel stays
      // on-screen if the bell is near the viewport bottom.
      const desiredTop = rect.top
      const maxTop = Math.max(8, window.innerHeight - 100)
      setPanelCoords({
        top: Math.min(desiredTop, maxTop),
        left: rect.right + 8,
        width: PANEL_WIDTH,
      })
    } else {
      // topbar (mobile): drop down from the bell, anchored to its right edge.
      const width = Math.min(PANEL_WIDTH, window.innerWidth - 28)
      const left = Math.max(14, rect.right - width)
      setPanelCoords({ top: rect.bottom + 6, left, width })
    }
    setPanelOpen(true)
  }

  const closePanel = () => setPanelOpen(false)

  useEffect(() => {
    if (!panelOpen) return
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (panelRef.current?.contains(t)) return
      if (triggerRef.current?.contains(t)) return
      closePanel()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closePanel() }
    const onResize = () => closePanel()
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onResize)
    }
  }, [panelOpen])

  // ─── Mark-read handlers ────────────────────────────────────────────────────
  // Broadcast nuance: mark_notification_read on a target_user_id=NULL row
  // flips read_at for everyone in the workspace (shared state). Documented
  // v1 limitation in the migration; current only type 'vendor_assignment_
  // escalation' always targets the owner specifically so it's invisible.
  const markRead = (id: string) => {
    queryClient.setQueryData<Notification[]>(listKey, (old) =>
      (old ?? []).map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
    queryClient.setQueryData<number>(countKey, (c) => Math.max(0, (c ?? 0) - 1))
    void supabase.rpc('mark_notification_read', { notification_id: id })
      .then(({ error }) => {
        if (error) {
          queryClient.invalidateQueries({ queryKey: listKey })
          queryClient.invalidateQueries({ queryKey: countKey })
          toast.error(`Couldn't mark read: ${error.message}`)
        }
      })
  }

  const handleRowClick = (n: Notification) => {
    if (!n.read_at) markRead(n.id)
    closePanel()
    if (n.link) navigate(n.link)
  }

  const handleMarkAllRead = async () => {
    const unread = items.filter(n => !n.read_at)
    if (unread.length === 0) return
    const nowIso = new Date().toISOString()
    queryClient.setQueryData<Notification[]>(listKey, (old) =>
      (old ?? []).map(n => n.read_at ? n : { ...n, read_at: nowIso }))
    queryClient.setQueryData<number>(countKey, (c) => Math.max(0, (c ?? 0) - unread.length))
    const results = await Promise.all(
      unread.map(n => supabase.rpc('mark_notification_read', { notification_id: n.id })),
    )
    const failed = results.filter(r => r.error)
    if (failed.length > 0) {
      queryClient.invalidateQueries({ queryKey: listKey })
      queryClient.invalidateQueries({ queryKey: countKey })
      toast.error(`Couldn't mark ${failed.length} notification${failed.length === 1 ? '' : 's'} read`)
    }
  }

  // ─── Render: trigger ───────────────────────────────────────────────────────
  const triggerLabel = unreadCount > 99 ? '99+' : String(unreadCount)
  const hasUnread = unreadCount > 0

  if (!wsId || !pid) return null

  return (
    <>
      {variant === 'sidebar' ? (
        <SidebarTrigger
          ref={triggerRef as React.Ref<HTMLButtonElement>}
          collapsed={collapsed}
          hasUnread={hasUnread}
          countLabel={triggerLabel}
          active={panelOpen}
          onClick={() => (panelOpen ? closePanel() : openPanel())}
        />
      ) : (
        <TopbarTrigger
          ref={triggerRef as React.Ref<HTMLButtonElement>}
          hasUnread={hasUnread}
          countLabel={triggerLabel}
          active={panelOpen}
          onClick={() => (panelOpen ? closePanel() : openPanel())}
        />
      )}

      {panelOpen && panelCoords && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Notifications"
          style={{
            position: 'fixed',
            top: panelCoords.top,
            left: panelCoords.left,
            width: panelCoords.width,
            maxHeight: '70vh',
            background: 'var(--surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-popover-mid)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>Notifications</span>
            {hasUnread && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--accent-text)', fontSize: 11, fontWeight: 600,
                  padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {items.length === 0 ? (
              <div style={{ padding: '28px 14px', textAlign: 'center', color: 'var(--ink-tertiary)', fontSize: 12 }}>
                No notifications yet
              </div>
            ) : (
              items.map(n => <NotificationRow key={n.id} n={n} onClick={() => handleRowClick(n)} />)
            )}
          </div>

          {items.length === PANEL_LIST_LIMIT && (
            <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border-subtle)', fontSize: 10, color: 'var(--ink-tertiary)', textAlign: 'center' }}>
              Showing latest {PANEL_LIST_LIMIT}
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  )
}

// ─── Triggers ─────────────────────────────────────────────────────────────────

const SidebarTrigger = React.forwardRef<HTMLButtonElement, {
  collapsed: boolean; hasUnread: boolean; countLabel: string; active: boolean; onClick: () => void;
}>(({ collapsed, hasUnread, countLabel, active, onClick }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      title={collapsed ? 'Notifications' : undefined}
      aria-label="Notifications"
      style={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        justifyContent: collapsed ? 'center' : 'flex-start',
        padding: collapsed ? '8px' : '7px 10px',
        background: active ? 'var(--sidebar-faint-bg)' : 'none',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        color: 'var(--sidebar-meta-ink)',
        textAlign: 'left',
        transition: 'background 150ms ease, color 150ms ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--sidebar-faint-bg)' }}
      onMouseLeave={e => { e.currentTarget.style.background = active ? 'var(--sidebar-faint-bg)' : 'none' }}
    >
      <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
        <Bell style={{ width: 15, height: 15 }} />
        {collapsed && hasUnread && (
          <span style={countOverlayStyle} aria-hidden>{countLabel}</span>
        )}
      </span>
      {!collapsed && (
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflow: 'hidden', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Notifications
          </span>
          {hasUnread && <span style={countPillStyle}>{countLabel}</span>}
        </span>
      )}
    </button>
  )
})
SidebarTrigger.displayName = 'SidebarTrigger'

const TopbarTrigger = React.forwardRef<HTMLButtonElement, {
  hasUnread: boolean; countLabel: string; active: boolean; onClick: () => void;
}>(({ hasUnread, countLabel, active, onClick }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-label="Notifications"
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: active ? 'var(--hover-bg)' : 'none',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        color: 'var(--ink-primary)',
        padding: 8,
        flexShrink: 0,
      }}
    >
      <Bell style={{ width: 20, height: 20 }} />
      {hasUnread && (
        <span style={countOverlayStyle} aria-hidden>{countLabel}</span>
      )}
    </button>
  )
})
TopbarTrigger.displayName = 'TopbarTrigger'

// ─── Row ──────────────────────────────────────────────────────────────────────

function NotificationRow({ n, onClick }: { n: Notification; onClick: () => void }) {
  const unread = !n.read_at
  const time = (() => {
    try { return formatDistanceToNow(parseISO(n.created_at), { addSuffix: true }) }
    catch { return '' }
  })()
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        gap: 10,
        padding: '12px 14px',
        background: 'none',
        border: 'none',
        borderBottom: '1px solid var(--border-subtle)',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background 100ms',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'none' }}
    >
      <span style={{
        flexShrink: 0,
        width: 7, height: 7, borderRadius: '50%',
        background: unread ? 'var(--accent)' : 'transparent',
        marginTop: 7,
      }} aria-hidden />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: 'block',
          fontSize: 13,
          fontWeight: unread ? 700 : 500,
          color: unread ? 'var(--ink-primary)' : 'var(--ink-secondary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          marginBottom: 2,
        }} title={n.title}>
          {n.title}
        </span>
        {n.body && (
          <span style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: 12,
            color: 'var(--ink-tertiary)',
            lineHeight: 1.45,
            marginBottom: 4,
          }} title={n.body}>
            {n.body}
          </span>
        )}
        <span style={{ display: 'block', fontSize: 10, color: 'var(--ink-tertiary)' }}>
          {time}
        </span>
      </span>
    </button>
  )
}

// ─── Badge styles (used by both triggers) ────────────────────────────────────

const countPillStyle: React.CSSProperties = {
  minWidth: 18,
  padding: '1px 6px',
  borderRadius: 100,
  background: 'var(--accent)',
  color: 'var(--accent-ink)',
  fontSize: 10,
  fontWeight: 700,
  textAlign: 'center',
  lineHeight: 1.5,
  flexShrink: 0,
}

const countOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: -4,
  right: -6,
  minWidth: 16,
  padding: '0 4px',
  height: 16,
  borderRadius: 100,
  background: 'var(--accent)',
  color: 'var(--accent-ink)',
  fontSize: 9,
  fontWeight: 800,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  boxShadow: '0 0 0 2px var(--sidebar-bg)',
}
